import { keepsStorefrontClub } from '@/features/clubs/storefrontReturn'
import { isStorefrontSectionSegment } from '@/features/clubs/storefrontRoutes'

/**
 * Resolve the chrome context for a pathname (site-relaunch Wave 1). Every route
 * lands in exactly one of five contexts; the shell picks its wrapper from this.
 * Order matters: auth → app → player → default-marketing. `/health` `Navigate`s
 * to `/`; the catch-all renders the 404 page, which resolves to marketing by
 * falling through (a junk path under a player prefix, e.g. `/clubs/a/b`, keeps
 * that prefix's chrome — intended). Legacy redirects carry no chrome of their
 * own — the browser lands on the canonical target and that pathname decides. Spec:
 * docs/ux/2026-07-18-site-relaunch-header-and-nav.md §1–2.
 *
 * `storefront` is the one context a pathname alone cannot decide (Club Storefront,
 * brick 1): it also needs to know that the club at that address has been READ and has
 * a page of its own, so that answer is passed in and defaults to "not yet". It used to
 * be the club's paid flag; since 2026-09-07 there is no such flag and every club with a
 * public slug qualifies (`features/clubs/storefront.ts`). What did NOT change is the
 * shape of this argument or its default: the shell must keep rendering the ordinary
 * player chrome until the club's own read lands, because a `storefront` answer with no
 * identity behind it would leave `RoutedShell` with nothing to dress the page in.
 * Everything else here still resolves from the pathname and nothing else. Which
 * pathnames a storefront may claim is `storefrontSlugFor` below — since brick 2, the
 * club's own page plus the club-owned sections named in `features/clubs/
 * storefrontRoutes.ts`.
 */
export type ChromeMode = 'auth-minimal' | 'app' | 'player' | 'marketing' | 'storefront'

const AUTH_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/set-password',
  '/delete-account',
  '/auth/callback',
  '/account/restore',
  '/forbidden',
  // Transient PayPal popup landing pages — logo-only chrome; they capture the
  // order, signal their opener, and self-close.
  '/payments/paypal/return',
  '/payments/paypal/cancel',
  // CardNet return landing pages — logo-only chrome; the player lands here full-page
  // after the redirect and the page confirms the pull-based result (brick C4).
  '/payments/cardnet/return',
  '/payments/cardnet/cancel',
  // AZUL return landing pages — same logo-only chrome as CardNet (brick AZUL).
  '/payments/azul/return',
  '/payments/azul/cancel',
]

// Signed-in app surfaces (prefix match). Includes the club-staff/manage task
// routes that already render on the admin surface, plus the relocated
// matchmaking route `/play/games` (which must win over the public `/play` hub).
const APP_PREFIXES = [
  '/workspace',
  '/organization',
  '/admin',
  '/start',
  '/profile',
  '/my-events',
  '/me',
  '/claim',
  '/play/games',
  '/tournaments/new',
  '/ladders/new',
  '/players/new',
  '/players/edit',
]

// Public player / marketplace browse surfaces (prefix match), tested AFTER the
// app manage sub-routes above so e.g. /tournaments/:id/edit stays app.
const PLAYER_PREFIXES = ['/play', '/clubs', '/tournaments', '/ladders', '/players']

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

function isAppPath(pathname: string): boolean {
  if (APP_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix))) return true
  // Manage sub-routes that live under otherwise-player prefixes.
  if (pathname.startsWith('/tournaments/') && pathname.endsWith('/edit')) return true
  if (pathname.startsWith('/ladders/') && pathname.endsWith('/manage')) return true
  return false
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/**
 * THE PATHNAMES A STOREFRONT MAY TAKE OVER — a club's own page, `/clubs/<slug>`, and the
 * club-owned sections named beneath it (`/clubs/<slug>/schedule`, brick 2). `/clubs` is
 * the directory, which a club cannot claim; everything else under a club keeps the
 * ordinary chrome.
 *
 * THE THIRD SEGMENT IS ADMITTED BY NAME, never by counting to three. A membership bill
 * lives under the club too (`/clubs/<slug>/bill/<cycleId>`), and "any deeper path" would
 * hand a club the chrome of pages it does not own. Returns the slug so the chrome can
 * look the club up by the only key it has; `''` means "not a storefront pathname".
 */
export function storefrontSlugFor(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  if (segments[0] !== 'clubs') return ''
  if (segments.length === 2) return safeDecode(segments[1])
  if (segments.length === 3 && isStorefrontSectionSegment(segments[2])) return safeDecode(segments[1])
  return ''
}

/**
 * The pathnames whose chrome also depends on WHO is visiting. `/` (home split,
 * docs/ux/2026-09-23-home-split.md §3.1): signed in, the personal home in the app
 * chrome; otherwise the marketing home. `/help` and every guide under it
 * (docs/ux/2026-09-24-help-center.md §3.1): someone who tapped "?" in the workspace
 * keeps their sidebar; a visitor and every crawler get the marketing site. The
 * content, canonical and SEO tags are the same in both.
 */
function isVisitorDependent(pathname: string): boolean {
  return pathname === '/' || matchesPrefix(pathname, '/help')
}

/**
 * `signedInVisitor` is `features/home/personal/homeChoice.ts`'s answer and defaults to
 * "no", so every other caller, every other pathname and every crawler resolves exactly
 * as before.
 *
 * `clubRemembered` — this tab walked out of a club's storefront (`storefrontReturnTarget`
 * answered). It moves one thing: our Privacy and Terms, linked from the club's footer,
 * wear the player chrome with the club's bar instead of our sales site (/ux storefront-exits
 * §3.2 C, owner 2026-09-26). The `/help` precedent: one address, two chromes by visitor.
 * Defaults to "no", so a search visitor, a fresh tab and every crawler get marketing.
 */
export function resolveChrome(
  pathname: string,
  storefrontKnown = false,
  signedInVisitor = false,
  clubRemembered = false,
): ChromeMode {
  if (signedInVisitor && isVisitorDependent(pathname)) return 'app'
  if (AUTH_PATHS.some((prefix) => matchesPrefix(pathname, prefix))) return 'auth-minimal'
  if (isAppPath(pathname)) return 'app'
  if (PLAYER_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix))) {
    // A club's own page sheds the marketplace chrome. Until its identity is known —
    // a cold open, a slug that resolves to no club, a club that published none — the
    // page stays exactly the player surface every club page has today.
    return storefrontKnown && storefrontSlugFor(pathname) ? 'storefront' : 'player'
  }
  return clubRemembered && keepsStorefrontClub(pathname) ? 'player' : 'marketing'
}
